<template>
  <!-- This main div wraps the entire application. The 'dark' class will be toggled here to control the theme. -->
  <div :class="{ 'dark': isDarkMode }" class="min-h-screen bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200 transition-colors duration-300">
    
    <!-- Navigation Bar -->
    <header class="bg-white dark:bg-gray-800 shadow-md sticky top-0 z-50">
      <nav class="container mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex items-center justify-between h-16">
          <!-- Logo / App Name -->
          <div class="flex-shrink-0">
            <router-link :to="{ name: 'Dashboard' }" class="text-2xl font-bold text-indigo-600 dark:text-indigo-400">
              QuizMaster
            </router-link>
          </div>

          <!-- Navigation Links & Actions -->
          <div class="flex items-center space-x-4">
            <router-link v-if="isLoggedIn" :to="{ name: 'Dashboard' }" class="nav-link">Dashboard</router-link>
            <router-link v-if="isLoggedIn && isAdmin" :to="{ name: 'Admin' }" class="nav-link">Quizzes</router-link>
            <router-link v-if="isLoggedIn && isAdmin" :to="{ name: 'AdminUsers' }" class="nav-link">Users</router-link>
            
            <!-- Spacer to push subsequent items to the right -->
            <div class="flex-grow"></div>

            <!-- UPDATED: Added a link to the Rules page, visible to everyone -->
            <router-link :to="{ name: 'Rules' }" class="nav-link">Rules</router-link>
            
            <!-- Theme Toggle Button -->
            <button @click="toggleTheme" class="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
              <svg v-if="isDarkMode" xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
              </svg>
            </button>

            <!-- Logout Button -->
            <button v-if="isLoggedIn" @click="handleLogout" class="bg-red-500 hover:bg-red-600 text-white font-bold py-2 px-4 rounded-lg transition-colors duration-300">
              Logout
            </button>
          </div>
        </div>
      </nav>
    </header>

    <!-- Main Content Area -->
    <main>
      <router-view v-slot="{ Component }">
        <transition name="fade" mode="out-in">
          <component :is="Component" />
        </transition>
      </router-view>
    </main>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import { useRouter } from 'vue-router';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from './firebase/config';

const router = useRouter();
const user = ref(null);
const isAdmin = ref(false);
const isDarkMode = ref(false);

const isLoggedIn = computed(() => !!user.value);

const toggleTheme = () => {
  isDarkMode.value = !isDarkMode.value;
};

watch(isDarkMode, (newVal) => {
  if (newVal) {
    document.documentElement.classList.add('dark');
    localStorage.setItem('theme', 'dark');
  } else {
    document.documentElement.classList.remove('dark');
    localStorage.setItem('theme', 'light');
  }
});

const handleLogout = async () => {
  try {
    await signOut(auth);
    router.push({ name: 'Home' });
  } catch (error) {
    console.error("Error signing out: ", error);
  }
};

onMounted(() => {
  const savedTheme = localStorage.getItem('theme');
  if (savedTheme === 'dark') {
    isDarkMode.value = true;
  }

  onAuthStateChanged(auth, async (currentUser) => {
    if (currentUser) {
      user.value = currentUser;
      const userDocRef = doc(db, 'users', currentUser.uid);
      const userDocSnap = await getDoc(userDocRef);
      if (userDocSnap.exists()) {
        isAdmin.value = userDocSnap.data().isAdmin === true;
      } else {
        isAdmin.value = false;
      }
    } else {
      user.value = null;
      isAdmin.value = false;
    }
  });
});
</script>

<style>
.nav-link {
  @apply text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 font-medium px-3 py-2 rounded-md text-sm transition-colors duration-300;
}

.router-link-exact-active {
  @apply text-indigo-600 dark:text-indigo-400;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
